import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol, MultiSelectChip, withStyles, type AccountUserRole, type UserRoleAssignment } from 'vopay';
import { Button, Title } from '#shared/components/UI';
import Dialog from '#shared/components/Dialog/Dialog';
import { DialogComponentProps } from '#shared/providers/Dialog/DialogContext';
import styles from './ManageUserRolesDialog.module.scss';

const cn = withStyles(styles);

/** The role diff to submit, or a request to open Create Role instead. The caller does the actual
 * assign/unassign calls and its own toast/error handling once this resolves — this dialog only
 * picks what the user wants, matching AddBillingDialog. */
export type ManageUserRolesResult =
  | { action: 'assign'; added: number[]; removed: number[] }
  | { action: 'createRole' };

export type ManageUserRolesDialogProps = DialogComponentProps<ManageUserRolesResult> & {
  user: { Username: string };
  availableRoles: AccountUserRole[];
  /** With it the role dropdown ends in a "+ New Role" button; only pass it for users who can create roles. */
  canCreateRole?: boolean;
  /** Passed explicitly rather than read via useUsers(): dialog.open() mounts this outside the Users
   * feature's own provider tree, so the feature context isn't reachable here. */
  fetchUserRoleAssignments: (params: { Username: string }) => Promise<UserRoleAssignment[]>;
};

type RoleOption = { value: string; label: string };

// Picks the role assignment diff for one user; the multi-select is edited locally and the difference
// from what the user already holds is resolved on Confirm. Content-only: the caller renders it through
// useDialog(), which supplies the Dialog chrome.
export default function ManageUserRolesDialog({
  user,
  availableRoles,
  canCreateRole,
  fetchUserRoleAssignments,
  onConfirm,
  onCancel,
}: ManageUserRolesDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ManageUserRolesDialog' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });
  const [initialRoleIDs, setInitialRoleIDs] = useState<number[]>([]);
  const [pendingRoleIDs, setPendingRoleIDs] = useState<number[]>([]);
  const [isFetching, setIsFetching] = useState(true);

  // Loads once: the dialog unmounts on close (useDialog), so a reopen is a fresh mount and fresh fetch.
  useEffect(() => {
    let isStale = false;

    async function load() {
      try {
        const assigned = await fetchUserRoleAssignments({ Username: user.Username });

        if (isStale) return;

        const roleIDs = assigned.map((role) => role.RoleID);

        setInitialRoleIDs(roleIDs);
        setPendingRoleIDs(roleIDs);
      } catch {
        // unwrap has already logged; with nothing loaded the user starts with no roles.
        if (isStale) return;

        setInitialRoleIDs([]);
        setPendingRoleIDs([]);
      } finally {
        if (!isStale) setIsFetching(false);
      }
    }

    load();

    return () => {
      isStale = true;
    };
  }, []);

  const options: RoleOption[] = availableRoles.map((role) => ({
    value: String(role.RoleID),
    label: role.Name,
  }));

  function handleChange(_event: ChangeEvent<HTMLInputElement>, args: { options: RoleOption[] }) {
    setPendingRoleIDs(args.options.map((opt) => Number(opt.value)));
  }

  function handleDelete(value: string) {
    setPendingRoleIDs((prev) => prev.filter((id) => String(id) !== value));
  }

  function handleConfirm() {
    const added = pendingRoleIDs.filter((id) => !initialRoleIDs.includes(id));
    const removed = initialRoleIDs.filter((id) => !pendingRoleIDs.includes(id));

    // Nothing to submit: same as Cancel.
    if (added.length === 0 && removed.length === 0) {
      onCancel();

      return;
    }

    onConfirm({ action: 'assign', added, removed });
  }

  return (
    <div className={cn('body')}>
      <Dialog.Title hidden>{t('title', { defaultValue: 'Manage Role(s)' })}</Dialog.Title>
      <MaterialSymbol icon="groups" fillAmount={1} size="40px" className={cn('icon')} />

      <Title level="4" className={cn('title')}>
        {t('title', { defaultValue: 'Manage Role(s)' })}
      </Title>

      <p className={cn('subtitle')}>
        {t('subtitle', { defaultValue: 'Select all the applicable role(s) to ' })}
        <span className={cn('subtitleEmail')}>{user.Username}</span>
      </p>

      <MultiSelectChip
        name="selectRoleMultiSelect"
        options={options}
        value={pendingRoleIDs.map(String)}
        onChange={handleChange}
        onDelete={handleDelete}
        placeholder={t('placeholder', { defaultValue: 'Select' })}
        filterable
        emptyFilterMsg={t('noRolesFound', { defaultValue: 'No roles found' })}
        disabled={isFetching}
        renderOption={(o, { Option, SelectedIcon, selected }) => (
          <Option>
            {o.label}
            {selected && <SelectedIcon />}
          </Option>
        )}
        renderDropdownFooter={
          canCreateRole
            ? ({ close }) => (
                <Button
                  iconLeft="add"
                  size="small"
                  onClick={() => {
                    close();
                    onConfirm({ action: 'createRole' });
                  }}
                >
                  {t('newRole', { defaultValue: 'New Role' })}
                </Button>
              )
            : undefined
        }
      />

      <div className={cn('footer')}>
        <Button colorScheme="secondary" onClick={onCancel}>
          {tButtons('cancel', { defaultValue: 'Cancel' })}
        </Button>

        <Button colorScheme="primary" onClick={handleConfirm} disabled={isFetching}>
          {tButtons('confirm', { defaultValue: 'Confirm' })}
        </Button>
      </div>
    </div>
  );
}
